import React, { useEffect, useState } from "react";
import { db } from "../../../../../firebase";
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  query,
  where 
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";

export default function CompanyProjects() {
  const [projects, setProjects] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompanyAndProjects = async () => {
      try {
        setLoading(true);
        setError(null);

        const auth = getAuth();
        const user = auth.currentUser;
        const storedData = localStorage.getItem("companyData");

        // 🔒 Çift koruma (auth + localStorage)
        if (!user || !storedData) {
          navigate("/login_corporate");
          return;
        }

        const parsedData = JSON.parse(storedData);
        const email = parsedData?.email;
        const taxNumber = parsedData?.taxNumber;
        
        if (!email || !taxNumber) {
          setError("Geçersiz oturum verisi.");
          setLoading(false);
          return;
        }

        // 🔹 Şirket bilgisini dashboard ile aynı şekilde çek
        const companiesRef = collection(db, "companies");
        const q = query(
          companiesRef, 
          where("taxInfo.taxNumber", "==", Number(taxNumber))
        );
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setError("Şirket bilgisi bulunamadı.");
          setLoading(false);
          return;
        }

        const docSnap = snapshot.docs[0];
        const companyData = docSnap.data();
        const companyId = docSnap.id; // Firestore doküman ID'si

        // Yetkili kişi bilgilerini çek
        let personData = null;
        try {
          const personRef = doc(db, "companies", companyId, "authorized_person", "personel_info");
          const personSnap = await getDoc(personRef);
          if (personSnap.exists()) {
            personData = personSnap.data();
          }
        } catch (error) {
          console.warn("Yetkili kişi bilgisi bulunamadı:", error);
        }

        const companyInfo = {
          id: companyId,
          company_id: companyData.company_id || "",
          company_name: companyData.company_name || companyData.company_id || "",
          taxInfo: companyData.taxInfo || {
            taxNumber: Number(taxNumber),
            companyType: "",
          },
          email: companyData.email || email || "",
          phone: companyData.phone || (personData?.person_phone || ""),
        };

        setCompany(companyInfo);

        // 1️⃣ Şirketin altındaki 'projects' alt koleksiyonunu al
        try {
          const companyProjectsRef = collection(db, "companies", companyId, "projects");
          const companyProjectsSnap = await getDocs(companyProjectsRef);

          if (companyProjectsSnap.empty) {
            setProjects([]);
            setLoading(false);
            return;
          }

          // 2️⃣ Sadece aktif projeleri filtrele
          const activeProjectIds = companyProjectsSnap.docs
            .filter((d) => {
              const data = d.data();
              return data.exist === true || data.exist === undefined; // exist undefined ise de göster
            })
            .map((d) => d.id);

          if (activeProjectIds.length === 0) {
            setProjects([]);
            setLoading(false);
            return;
          }

          // 3️⃣ Ana 'projects' koleksiyonundan detayları getir
          const projectPromises = activeProjectIds.map(async (projectId) => {
            try {
              const projectRef = doc(db, "projects", projectId);
              const projectSnap = await getDoc(projectRef);
              if (projectSnap.exists()) {
                return { 
                  id: projectId, 
                  ...projectSnap.data(),
                  // Eksik alanlar için default değerler
                  name: projectSnap.data().name || projectSnap.data().project_name || "İsimsiz Proje",
                  city: projectSnap.data().city || "Belirtilmemiş",
                  price: projectSnap.data().price || 0,
                  status: projectSnap.data().status || "Aktif"
                };
              }
              return null;
            } catch (error) {
              console.error(`Proje ${projectId} yüklenirken hata:`, error);
              return null;
            }
          });

          const fetchedProjects = (await Promise.all(projectPromises)).filter(Boolean);
          setProjects(fetchedProjects);
        } catch (projectsError) {
          console.error("Projeler yüklenirken hata:", projectsError);
          // Proje koleksiyonu yoksa boş array döndür
          setProjects([]);
        }
      } catch (err) {
        console.error("Veri yükleme hatası:", err);
        setError("Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyAndProjects();
  }, [navigate]);

  const handleProjectClick = (projectId) => {
    localStorage.setItem("selectedProjectId", projectId);
    navigate("/company_project_details");
  };

  const handleAddProject = () => {
    navigate("/add-project");
  };

  const handleBackToDashboard = () => {
    navigate("/dashboard_corporate");
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Projeler yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorText}>{error}</p>
        <button 
          style={styles.backButton}
          onClick={() => navigate("/dashboard_corporate")}
        >
          Dashboard'a Dön
        </button>
      </div>
    );
  }

  return (
    <div style={styles.mainContainer}>
      <div style={styles.container}>
        {/* Üst Bilgi ve Butonlar */}
        <div style={styles.header}>
          <div style={styles.headerInfo}>
            <h2 style={styles.heading}>
              {company?.company_name || company?.company_id || "Şirket"} • Projeler
            </h2>
            <div style={styles.companyInfo}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Vergi No:</span>
                <span style={styles.infoValue}>{company?.taxInfo?.taxNumber || "—"}</span>
              </div>
              {company?.taxInfo?.companyType && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Şirket Türü:</span>
                  <span style={styles.infoValue}>{company.taxInfo.companyType}</span>
                </div>
              )}
            </div>
          </div>
          <div style={styles.headerButtons}>
            <button 
              style={styles.backButton}
              onClick={handleBackToDashboard}
            >
              Dashboard'a Dön
            </button>
            <button 
              style={styles.addButton}
              onClick={handleAddProject}
            >
              + Yeni Proje Ekle
            </button>
          </div>
        </div>

        {/* Bilgilendirme Kutusu */}
        {projects.length === 0 && (
          <div style={styles.infoBanner}>
            <span style={styles.infoIcon}>📋</span>
            <span>
              Henüz hiç projeniz bulunmuyor. Yeni bir proje eklemek için "Yeni Proje Ekle" butonunu kullanabilirsiniz.
            </span>
          </div>
        )}

        {/* Proje Kartları */}
        {projects.length > 0 ? (
          <>
            <div style={styles.stats}>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>{projects.length}</div>
                <div style={styles.statLabel}>Toplam Proje</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>
                  {projects.filter(p => p.status === "Aktif").length}
                </div>
                <div style={styles.statLabel}>Aktif Proje</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>
                  {projects.filter(p => p.status === "Tamamlandı").length}
                </div>
                <div style={styles.statLabel}>Tamamlanan</div>
              </div>
            </div>

            <div style={styles.grid}>
              {projects.map((project) => (
                <div
                  key={project.id}
                  style={styles.card}
                  onClick={() => handleProjectClick(project.id)}
                >
                  <div style={styles.cardHeader}>
                    <h3 style={styles.projectName}>
                      {project.name || "İsimsiz Proje"}
                    </h3>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: getStatusColor(project.status).bg,
                      color: getStatusColor(project.status).text,
                    }}>
                      {project.status || "Aktif"}
                    </span>
                  </div>
                  <div style={styles.cardBody}>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Şehir:</span>
                      <span style={styles.detailValue}>{project.city || "Belirtilmemiş"}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Fiyat:</span>
                      <span style={styles.detailValue}>
                        {project.price ? `${project.price.toLocaleString("tr-TR")} ₺` : "Belirtilmemiş"}
                      </span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Oluşturulma:</span>
                      <span style={styles.detailValue}>
                        {project.createdAt 
                          ? new Date(project.createdAt.seconds * 1000).toLocaleDateString("tr-TR")
                          : "—"}
                      </span>
                    </div>
                    {project.description && (
                      <div style={styles.description}>
                        <span style={styles.detailLabel}>Açıklama:</span>
                        <p style={styles.detailValue}>
                          {project.description.length > 100 
                            ? `${project.description.substring(0, 100)}...` 
                            : project.description}
                        </p>
                      </div>
                    )}
                  </div>
                  <div style={styles.cardFooter}>
                    <span style={styles.viewText}>Detayları Gör →</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📁</div>
            <h3 style={styles.emptyTitle}>Henüz Projeniz Yok</h3>
            <p style={styles.emptyText}>
              Şu anda görüntülenecek proje bulunmuyor. İlk projenizi oluşturmak için aşağıdaki butonu kullanabilirsiniz.
            </p>
            <button 
              style={styles.emptyButton}
              onClick={handleAddProject}
            >
              İlk Projeyi Oluştur
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Duruma göre renk belirleme
const getStatusColor = (status) => {
  const statusLower = status?.toLowerCase();
  switch (statusLower) {
    case 'aktif':
      return { bg: '#dcfce7', text: '#166534' };
    case 'tamamlandı':
      return { bg: '#dbeafe', text: '#1e40af' };
    case 'iptal':
      return { bg: '#fee2e2', text: '#991b1b' };
    case 'beklemede':
      return { bg: '#fef3c7', text: '#92400e' };
    default:
      return { bg: '#f3f4f6', text: '#4b5563' };
  }
};

// 🎨 Styles - Dashboard ile uyumlu
const styles = {
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "1200px",
    margin: "20px auto",
    padding: "0 16px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #0b63d6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "16px",
  },
  loadingText: {
    fontSize: "14px",
    color: "#666",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
    textAlign: "center",
    padding: "40px 20px",
  },
  errorText: {
    fontSize: "16px",
    color: "#dc2626",
    marginBottom: "20px",
    fontWeight: "500",
  },
  container: {
    padding: "24px",
    backgroundColor: "#f7f9fb",
    borderRadius: "12px",
    boxShadow: "0 8px 30px rgba(8,30,70,0.04)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    paddingBottom: "20px",
    borderBottom: "1px solid #e5e7eb",
    flexWrap: "wrap",
    gap: "16px",
  },
  headerInfo: {
    flex: 1,
    minWidth: "300px",
  },
  heading: {
    fontSize: "24px",
    color: "#0b2b5a",
    marginBottom: "12px",
    textAlign: "left",
  },
  companyInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  infoLabel: {
    fontSize: "13px",
    color: "#6b7280",
    fontWeight: "500",
  },
  infoValue: {
    fontSize: "13px",
    color: "#111827",
    fontWeight: "500",
  },
  headerButtons: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  backButton: {
    backgroundColor: "#6b7280",
    color: "white",
    border: "none",
    padding: "10px 18px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.2s",
    whiteSpace: "nowrap",
  },
  addButton: {
    backgroundColor: "#0b63d6",
    color: "white",
    border: "none",
    padding: "10px 18px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.2s",
    whiteSpace: "nowrap",
  },
  infoBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    fontSize: "13px",
    backgroundColor: "#eff6ff",
    color: "#1e40af",
    padding: "14px 18px",
    borderRadius: "8px",
    marginBottom: "24px",
    border: "1px solid #dbeafe",
  },
  infoIcon: {
    fontSize: "16px",
    color: "#1e40af",
    flexShrink: 0,
    marginTop: "2px",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "32px",
  },
  statCard: {
    backgroundColor: "#ffffff",
    borderRadius: "10px",
    padding: "20px",
    textAlign: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    border: "1px solid #e5e7eb",
  },
  statNumber: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#0b2b5a",
    marginBottom: "8px",
  },
  statLabel: {
    fontSize: "14px",
    color: "#6b7280",
    fontWeight: "500",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "24px",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "1px solid #f3f4f6",
  },
  projectName: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#0b2b5a",
    margin: 0,
    flex: 1,
  },
  statusBadge: {
    fontSize: "11px",
    fontWeight: "600",
    padding: "4px 10px",
    borderRadius: "20px",
    whiteSpace: "nowrap",
    marginLeft: "12px",
  },
  cardBody: {
    flex: 1,
    marginBottom: "16px",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    paddingBottom: "10px",
    borderBottom: "1px dashed #f3f4f6",
  },
  detailLabel: {
    fontSize: "13px",
    color: "#6b7280",
    fontWeight: "500",
  },
  detailValue: {
    fontSize: "13px",
    color: "#111827",
    fontWeight: "400",
    textAlign: "right",
    maxWidth: "60%",
    wordBreak: "break-word",
  },
  description: {
    marginTop: "12px",
    paddingTop: "12px",
    borderTop: "1px dashed #f3f4f6",
  },
  cardFooter: {
    textAlign: "right",
    paddingTop: "12px",
    borderTop: "1px solid #f3f4f6",
  },
  viewText: {
    fontSize: "13px",
    color: "#0b63d6",
    fontWeight: "500",
    textDecoration: "none",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    border: "2px dashed #e5e7eb",
    marginTop: "20px",
  },
  emptyIcon: {
    fontSize: "64px",
    marginBottom: "20px",
    color: "#9ca3af",
  },
  emptyTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "12px",
  },
  emptyText: {
    fontSize: "15px",
    color: "#6b7280",
    maxWidth: "500px",
    margin: "0 auto 24px",
    lineHeight: "1.6",
  },
  emptyButton: {
    backgroundColor: "#0b63d6",
    color: "white",
    border: "none",
    padding: "12px 28px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "500",
    transition: "background-color 0.2s",
  },
};

// Spinner animation için inline CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);